import React, {
  useState,
  useEffect,
  useMemo,
  Fragment,
  forwardRef,
  FC,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Checkbox,
  FileSelector,
  InputAdvanced,
  SelectAdvanced,
} from "@crowd/ui-kit";
import IdeaService from "../../../../services/ideaService";
import { getProjects } from "../../../../store/actions/IdeasActions";

import { UserTypeEnum } from "../../../../types/User.interface";
import EventsTrackClickCheckbox from "../../EventsTrack/wrappers/helpers/EventsTrackClickCheckbox";
import EventsTrackClickTextarea from "../../EventsTrack/wrappers/helpers/EventsTrackClickTextarea";
import EventsTrackClickInput from "../../EventsTrack/wrappers/helpers/EventsTrackClickInput";
import EventsTrackClickFileSelectorLabel from "../../EventsTrack/wrappers/helpers/EventsTrackClickFileSelectorLabel";
import EventsTrackClickSelectInput from "../../EventsTrack/wrappers/helpers/EventsTrackClickSelectInput";
import EventsTrackClickSelectItem from "../../EventsTrack/wrappers/helpers/EventsTrackClickSelectItem";
import { PhotoIdeaAddressType } from "../../../../types/Photo.interface";
import Map from "../../../presentational/Map/Map";
import EventsTrackWrapperClick from "../../EventsTrack/wrappers/EventsTrackWrapperClick";
import { selectUserDetails } from "../../../../store/selectors/profile";
import { selectIdeasProjects } from "../../../../store/selectors/idea";
import {
  ProjectCategoryTagDto,
  ProjectIdeaItemDto,
} from "../../../../types/Idea.interface";
import { AppModalProps } from "../../Modals/AppModal/AppModal";
import styles from "./PhotoIdeaForm.module.scss";
import ConfirmModal from "../../Modals/ConfirmModal/ConfirmModal";
import { useConfirm } from "../../../../hooks/useConfirm";
import { ApiStatusCode } from "../../../../types/Common.interface";
import { useIsMobile } from "../../../../hooks/useMedia";
import Text from "../../../presentational/Typography/Text";
import AddressService from "../../../../services/addressService";
import useAbortController from "../../../../hooks/useAbortController";
import { debounce } from "../../../../utils";
import EventsTrackClickSelectInputEditable from "../../EventsTrack/wrappers/helpers/EventsTrackClickSelectInputEditable";
import { searchRequestNotFoundMessage } from "../../../../utils/constants";

export interface PhotoIdeaFormProps
  extends Pick<AppModalProps, "isOpen" | "onClose"> {
  projectsList?: ProjectCategoryTagDto[];
  projectCategoryTagId?: string;
  photoId?: string;
  onSuccessSubmitData?: (data: any) => void;
  onSuccessSubmit?: () => void;
}

const maxTotalSizeMB = 15;
const maxFiles = 3;
const maxAllowedCommentLength = 200;

const SelectInputWrapper = forwardRef<HTMLInputElement>((props, ref) => (
  <EventsTrackClickInput ref={ref} {...props} label="Начните вводить адрес" />
));

const PhotoIdeaForm: FC<PhotoIdeaFormProps> = ({
  isOpen,
  onClose,
  projectsList,
  projectCategoryTagId,
  photoId,
  onSuccessSubmitData,
  onSuccessSubmit,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const isPhone = useIsMobile();
  const user = useSelector(selectUserDetails);
  const projectsStore = useSelector(selectIdeasProjects);
  const projects = projectsList || projectsStore;
  const projectsForSelect = useMemo(
    () =>
      projects?.map((project) => ({
        ...project,
        title: project.projectTitle || project.categoryTitle,
      })),
    [projects]
  );

  // ключ для ремонтирования селектов для сброса значений на предустановленные
  const [selectPresetTriggerCount, setSelectPresetTriggerCount] = useState(0);

  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [idea, setIdea] = useState<ProjectIdeaItemDto>(null);
  const [address, setAddress] = useState("");
  const [isAddressSearching, setIsAddressSearching] = useState(false);
  const { newCancelToken, isCancelSource } = useAbortController();
  const [addressesList, setAddressesList] = useState<string[]>([]);
  const [addressValidator, setAddressValidator] = useState<any>(null);
  const [specifyAddressValidator, setSpecifyAddressValidator] =
    useState<any>(null);
  const [mapErr, setMapErr] = useState("");
  const [images, setImages] = useState<any[]>([]);
  const [comment, setComment] = useState("");
  const [commentValidator, setCommentValidator] = useState<any>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectCustomInputUsed, setSelectCustomInputUsed] = useState(false);

  const addressFieldsValid = () => {
    if (idea?.addressType === PhotoIdeaAddressType.EMPTY) {
      return true;
    }

    if (idea?.addressType === PhotoIdeaAddressType.DIRECTLY) {
      return !!address;
    }

    // for preset address select
    if (idea?.addresses?.length) {
      if (selectCustomInputUsed) {
        // for custom input field of the select
        return specifyAddressValidator.isValid();
      }
      // for select
      return !!address;
    }
    // for simple address field
    return addressValidator?.isValid();
  };

  const formValid = () =>
    selectedProject &&
    (comment.length === 0 || commentValidator.isValid()) &&
    idea &&
    images.length &&
    addressFieldsValid() &&
    agreed;

  const searchAddresses = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query) {
          return;
        }

        setIsAddressSearching(true);

        try {
          const { data } = await AddressService.search(query, newCancelToken());
          setAddressesList(data.map(({ address }) => address));
          setIsAddressSearching(false);
          if (!data.length) {
            setMapErr(searchRequestNotFoundMessage);
          }
        } catch (e) {
          if (!isCancelSource(e)) {
            setIsAddressSearching(false);
            console.log(e);
          }
        }
      }, 500),
    [isCancelSource, newCancelToken]
  );

  const submitForm = async () => {
    setSubmitted(true);
    if (formValid()) {
      const formData = {
        ideaItemId: idea.id,
        attachmentIds: images.map((img) => img.id),
        ...(idea.addressType !== PhotoIdeaAddressType.EMPTY && { address }),
        ...(comment.length > 0 && { comment }),
      };
      const response = await IdeaService.sendIdea(formData);
      if (response && response.status === 0) {
        const getTextContent = () => {
          const baseMessage = "Фотография отправлена.";

          if (!user?.ssoid) {
            return baseMessage;
          }

          if (user.userType === UserTypeEnum.FULL) {
            return (
              <>
                {baseMessage} В случае успешного прохождения модерации за одну
                опубликованную фотографию по каждой идее вам будут начислены{" "}
                <a
                  href="https://crowd.mos.ru/pointsRules"
                  target="_blank"
                  rel="noreferrer"
                >
                  баллы
                </a>{" "}
                (<b>не более чем за 10 фотографий в месяц</b>).
              </>
            );
          }

          if (user.userType === UserTypeEnum.STANDARD) {
            return (
              <>
                {baseMessage} В случае успешного прохождения модерации за одну
                опубликованную фотографию по каждой идее вам будут начислены{" "}
                <a
                  href="https://crowd.mos.ru/pointsRules"
                  target="_blank"
                  rel="noreferrer"
                >
                  баллы
                </a>{" "}
                (<b>не более чем за 10 фотографий в месяц</b>).
              </>
            );
          }

          if (user.userType === UserTypeEnum.SHORT) {
            return (
              <>
                {baseMessage} При наличии полной или стандартной учетной записи
                на портале{" "}
                <a
                  href="https://my.mos.ru/my/#/"
                  target="_blank"
                  rel="noreferrer"
                >
                  mos.ru
                </a>{" "}
                вы сможете получать{" "}
                <a
                  href="https://crowd.mos.ru/pointsRules"
                  target="_blank"
                  rel="noreferrer"
                >
                  баллы
                </a>{" "}
                за опубликованные фото (
                <b>
                  за одну опубликованную фотографию по каждой идее, но не более
                  чем за 10 фотографий в месяц
                </b>
                ).
              </>
            );
          }

          return baseMessage;
        };

        onSuccessSubmitData?.(response.data);
        if (onSuccessSubmit) {
          onSuccessSubmit();
        } else {
          confirm({
            title: "Спасибо!",
            body: getTextContent(),
            confirmBtnText: "Хорошо",
            showCloseIcon: isPhone,
            showPhoneHeaderProfile: false,
            classNames: styles["success-modal"],
          });

          onClose();
        }
      }
      if (response && response.status === ApiStatusCode.UNKNOWN_ERROR) {
        confirm({
          body: "Некорректный формат фото.",
          confirmBtnText: "Хорошо",
          inConfirmBtnOutlined: true,
        });
      }
    }
  };

  useEffect(() => {
    if (!projects) {
      dispatch(getProjects());
    }
  }, []);

  useEffect(() => {
    if (projectCategoryTagId && projectsForSelect) {
      const project = projectsForSelect.find(
        ({ id }) => id === projectCategoryTagId
      );
      if (project) {
        setSelectedProject(project);
        if (photoId) {
          const photoIdea = [
            ...(project.items || []),
            ...(project.gorodIdeas || []),
          ].find(({ id }) => id === photoId);
          if (photoIdea) {
            setIdea(photoIdea);
          }
        }

        // для ремонтирования селектов с предустановленными значениями
        setSelectPresetTriggerCount((prev) => prev + 1);
      }
    }
  }, [projectsForSelect, projectCategoryTagId, photoId]);

  const addressValidateRules = {
    notEmpty: true,
    minLength: 5,
    maxLength: 90,
  };

  const renderContent = () => {
    const addressLabel = "Укажите адрес, где сделано изображение";

    const renderAnyAddressInput = () => (
      <InputAdvanced
        value={address}
        label={addressLabel}
        isInnerLabel
        isRequired
        isDisabled={!idea}
        onChange={setAddress}
        validateRules={addressValidateRules}
        maxLength={addressValidateRules.maxLength}
        onInitValidator={setAddressValidator}
        showError={submitted}
        shouldValidate
        ComponentInput={EventsTrackClickInput}
      />
    );

    const labelTheme = "Выберите проект или тему";
    return (
      <div className={styles.content}>
        <Fragment key={selectPresetTriggerCount}>
          <SelectAdvanced
            value={selectedProject}
            items={projectsForSelect}
            bindTo="title"
            label={labelTheme}
            isRequired
            isInnerLabel
            onItemSelect={setSelectedProject}
            isInvalid={(submitted && !selectedProject) || false}
            ComponentSelectInput={EventsTrackClickSelectInput}
            ComponentSelectItem={EventsTrackClickSelectItem}
          />

          <SelectAdvanced
            value={idea}
            items={
              selectedProject
                ? selectedProject.items || selectedProject.gorodIdeas
                : []
            }
            bindTo="title"
            label="Выберите идею"
            isInnerLabel
            isRequired
            onItemSelect={setIdea}
            isInvalid={(submitted && !idea) || false}
            ComponentSelectInput={EventsTrackClickSelectInput}
            ComponentSelectItem={EventsTrackClickSelectItem}
          />
        </Fragment>

        {!!idea?.photoInstruction && (
          <Text paragraph size={3} className={styles.instruction}>
            {idea.photoInstruction}
          </Text>
        )}

        <FileSelector
          buttonSize="m"
          text="Добавить файл"
          hint={(
            <span className={styles["selector-hint"]}>
              Размер изображения не должен превышать {maxTotalSizeMB} Мб,
              допустимы форматы .jpeg, .jpg, .png, .webp
            </span>
          )}
          isMultiple={false}
          upload
          allowedExtensions={[
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
          ]}
          maxTotalSizeMB={maxTotalSizeMB}
          maxFiles={maxFiles}
          onChange={setImages}
          isInvalid={(submitted && !images.length) || false}
          attachmentsLength={images?.length || 0}
          ComponentLabel={EventsTrackClickFileSelectorLabel}
        />

        {idea?.addressType === PhotoIdeaAddressType.DIRECTLY ? (
          <>
            <SelectAdvanced
              isRequired
              label={addressLabel}
              isInnerLabel
              value={address || null}
              items={addressesList}
              isLoading={isAddressSearching}
              onItemSelect={(item) => {
                setMapErr("");
                setAddress(item);
              }}
              contentEditable
              manualFilter
              onFilterChange={searchAddresses}
              isInvalid={submitted && (!address || !!mapErr)}
              error={mapErr}
              errorAsHint
              ComponentSelectInput={EventsTrackClickSelectInputEditable}
            />

            <Text size={2}>Выбранный адрес на карте:</Text>

            <EventsTrackWrapperClick
              id={["EMPTY", "CLICK_PARTIAL"]}
              replace={["Нажатие на карту"]}
            >
              <div>
                <Map
                  address={address}
                  onClick={(item) => setAddress(item?.address || item)}
                  onError={setMapErr}
                />
              </div>
            </EventsTrackWrapperClick>
          </>
        ) : idea?.addressType === PhotoIdeaAddressType.ANY ? (
          renderAnyAddressInput()
        ) : (
          ([
            PhotoIdeaAddressType.LIST,
            PhotoIdeaAddressType.DEFAULT_LIST,
          ].includes(idea?.addressType) ||
            !idea?.addressType) &&
          ((!!idea?.addresses?.length && (
            <SelectAdvanced
              value={address || null}
              items={(idea && idea.addresses) || []}
              label={addressLabel}
              isInnerLabel
              isRequired
              onItemSelect={setAddress}
              isInvalid={(submitted && !address) || false}
              inputOptions={
                idea?.addressType === PhotoIdeaAddressType.LIST
                  ? {
                      value: address,
                      onChange: setAddress,
                      isRequired: true,
                      label: "Введите адрес",
                      isInnerLabel: true,
                      validateRules: addressValidateRules,
                      onInitValidator: setSpecifyAddressValidator,
                      showError: submitted,
                      shouldValidate: true,
                      errorAsHint: true,
                      ComponentInput: SelectInputWrapper,
                    }
                  : undefined
              }
              onSelfSpecifyChange={(inputWasShown, inputIsShown) => {
                setSelectCustomInputUsed(inputIsShown);
                if (inputIsShown && !inputWasShown) {
                  setAddress("");
                }
              }}
              ComponentSelectInput={EventsTrackClickSelectInput}
              ComponentSelectItem={EventsTrackClickSelectItem}
            />
          )) ||
            renderAnyAddressInput())
        )}

        <InputAdvanced
          value={comment}
          label="Добавьте комментарий"
          isInnerLabel
          isRequired={false}
          isMultiline
          rows={1}
          maxLength={maxAllowedCommentLength}
          hint="Не более 200 символов"
          onChange={setComment}
          validateRules={{
            minLength: 5,
            maxLength: maxAllowedCommentLength,
            cyrillicWithPunctuationAndNumbers: true,
          }}
          onInitValidator={setCommentValidator}
          shouldValidate
          errorAsHint
          showError={submitted && comment.length > 0}
          ComponentInput={EventsTrackClickTextarea}
          className={styles.comment}
        />

        <div className={styles["rules-wrapper"]}>
          <Checkbox
            html={`Я ознакомился с <a data-click-id='[39]' data-replace='${JSON.stringify(
              ["Правилами модерации"]
            )}' href='/moderation' target='_blank'>Правилами модерации</a> и <a data-click-id='[39]' data-replace='${JSON.stringify(
              ["инструкциями по фотографированию"]
            )}' href='/photos#ideas' target='_blank'>инструкциями по фотографированию</a>`}
            onChange={setAgreed}
            isInvalid={(submitted && !agreed) || false}
            ComponentCheckbox={EventsTrackClickCheckbox}
          />
        </div>
      </div>
    );
  };

  return (
    <ConfirmModal
      isLoading={!projects}
      isOpen={isOpen}
      title="Загрузка фотографии"
      body={renderContent()}
      onClose={onClose}
      onConfirm={submitForm}
      classNames={styles["photo-idea-form"]}
      dontCloseOnConfirm
      showPhoneHeaderProfile={false}
    />
  );
};

export default React.memo(PhotoIdeaForm);
